/**
 * PostgREST `.or()` free-text search escaping.
 *
 * Free-text search is built by interpolating an operator-supplied term into a
 * PostgREST `or` filter:
 *
 *   .or('customer_name.ilike.*term*,customer_email.ilike.*term*')
 *
 * PostgREST parses that string structurally, so several characters are SYNTAX,
 * not data:
 *
 *   ,  separates filters within the or()   -> "Smith, John" becomes two filters
 *   .  separates column.operator.value     -> "a.b" shifts the operator
 *   () groups logic / wraps `in` lists
 *   :  used in embedded-resource targets
 *
 * On top of that, `ilike` treats `%` and `_` as wildcards, so a customer
 * searching for "50%" would otherwise match everything.
 *
 * Rather than attempt to escape each of these (PostgREST's quoting rules
 * interact awkwardly with `*`/`%` wildcard interpolation), we quote the value
 * and neutralise the wildcards. Quoting is what PostgREST documents for values
 * containing reserved characters, and it keeps a single filter a single filter.
 */

/** Characters that are structural in a PostgREST filter string. */
const RESERVED = /[,.():]/

/**
 * Escape a search term for safe interpolation into an `ilike` filter inside
 * `.or(...)`.
 *
 * Returns `null` when the term carries no searchable content, so callers can
 * skip `.or()` entirely rather than emitting a filter that matches everything.
 *
 * The returned string ALREADY includes the surrounding `*` wildcards and, where
 * needed, the double quotes — callers interpolate it directly:
 *
 *   const v = escapeOrTerm(input)
 *   if (v) query = query.or(`customer_name.ilike.${v},customer_email.ilike.${v}`)
 */
export function escapeOrTerm(term: string | null | undefined): string | null {
  if (term === null || term === undefined) return null

  const trimmed = term.trim()
  if (trimmed.length === 0) return null

  // Escape ilike wildcards so they match literally. Backslash first, or we
  // would double-escape the escapes we add next.
  const literal = trimmed
    .replace(/\\/g, '\\\\')
    .replace(/%/g, '\\%')
    .replace(/_/g, '\\_')

  // `*` is PostgREST's shorthand for `%` in ilike values. A literal asterisk in
  // the term would otherwise become a wildcard.
  const withoutStars = literal.replace(/\*/g, '\\*')

  const pattern = `*${withoutStars}*`

  // Double quotes inside a quoted PostgREST value are escaped by doubling.
  // We quote whenever the term contains reserved characters, whitespace, or a
  // quote — whitespace included because unquoted values terminate awkwardly in
  // some PostgREST versions.
  const needsQuoting =
    RESERVED.test(withoutStars) || /\s/.test(withoutStars) || withoutStars.includes('"')

  if (!needsQuoting) return pattern

  return `"${pattern.replace(/"/g, '""')}"`
}

/**
 * Build the full `.or()` argument for an ilike search across several columns.
 *
 * Returns `null` when there is nothing to search for, so the caller skips the
 * filter. Keeping this here (rather than in the query builder) means the exact
 * string that reaches PostgREST is unit-testable.
 */
export function buildIlikeOrFilter(
  term: string | null | undefined,
  columns: readonly string[]
): string | null {
  const value = escapeOrTerm(term)
  if (value === null || columns.length === 0) return null

  return columns.map((column) => `${column}.ilike.${value}`).join(',')
}
